import {
  faChevronDown,
  faChevronUp,
  faLock,
  faPencil,
  faPlus,
  faSave,
  faSpinner,
  faTimes,
  faTrash,
  faUnlock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, FieldPath, FieldValue, FieldValues, useForm } from 'react-hook-form';

import ArrayFieldEditor from '@/components/PersonInPlaceEditor/ArrayFieldEditor';
import { INSURANCE_POLICY_NOT_MUTABLE_EXEMPT_FIELDS } from '@/components/PersonInPlaceEditor/components/InPlaceEditor/constants';
import FieldEditor from '@/components/PersonInPlaceEditor/FieldEditor';
import type { Field } from '@/components/PersonInPlaceEditor/types';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { Person } from '@/types/person';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Heading from '@/ui/components/Heading';
import { hasPermission, PEOPLE_INSURANCE_POLICY_ADMINISTRATOR } from '@/utils/userPermission';

import { Props, RecordType } from './types';

/**
 * A form editor that allows for in-place editing of an object's fields
 */
const InPlaceEditor = <T extends FieldValues, R>({
  children,
  deleteMutation,
  disableEdit = false,
  disableNew = false,
  editButtonClass = '',
  fields,
  mutation,
  newRecord = false,
  newRecordButtonText = 'Add New',
  record,
  recordType,
  schema,
  sectionEditor,
  sendAllFields = false,
  submittedValuesManipulator,
  subsectionEditor,
  doubleClickToEdit = true,
  checkValidationMessage,
  initialIsEditing = false,
  onEditingChange
}: PropsWithChildren<Props<T, R>>) => {
  const { permissions } = useContext(AppContext);
  const [isEditing, setIsEditing] = useState(initialIsEditing);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    onEditingChange?.(isEditing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);
  const isFieldsArray = Array.isArray(fields);

  const handleDoubleClick = () => {
    if (!disableEdit) {
      setIsEditing(true);
    }
  };

  const {
    control,
    formState: { errors, dirtyFields },
    getValues,
    handleSubmit,
    reset,
    watch
  } = useForm({
    values: pick(record, isFieldsArray ? fields.map(({ key }) => key) : fields.key),
    ...(schema && { resolver: yupResolver(schema) })
  });
  const values = watch();

  // Mutability
  const canEditImmutableInsurancePolicies = hasPermission({
    permissions,
    requiredPermission: PEOPLE_INSURANCE_POLICY_ADMINISTRATOR
  });
  const [isMutable, setIsMutable] = useState<boolean>(
    getValues('is_mutable' as FieldPath<T>) ?? true
  );
  const isInsurancePolicy = recordType === RecordType.InsurancePolicy;
  const isMutableOverrides = isInsurancePolicy && canEditImmutableInsurancePolicies;
  const cannotBeEditedMessage = isInsurancePolicy
    ? 'This insurance policy is verified and cannot be edited.'
    : 'This record is immutable and cannot be edited.';
  // End Mutability

  useEffect(() => {
    setIsExpanded(false);
    reset();
  }, [isEditing, reset]);

  const visibleFields = useMemo(
    () => (isFieldsArray && fields.filter(({ hidden }) => !hidden)) || [],
    [fields, isFieldsArray]
  );
  const hiddenFields = useMemo(
    () => (isFieldsArray && fields.filter(({ hidden }) => hidden)) || [],
    [fields, isFieldsArray]
  );
  const hasHiddenFields = useMemo(() => !!hiddenFields.length, [hiddenFields]);

  const handleSave = async (values: Partial<T>) => {
    let valuesToSend: Pick<Partial<T>, string> = values;

    if (!sendAllFields) {
      const dirtyValues = pick(values, Object.keys(dirtyFields));
      const alwaysSendFields =
        Array.isArray(fields) &&
        fields.filter((field) => field.alwaysSend).map((field) => field.key);

      if (alwaysSendFields && alwaysSendFields.length) {
        alwaysSendFields.forEach((field) => {
          dirtyValues[field] = values[field];
        });
      }
      valuesToSend = dirtyValues;
    }

    submittedValuesManipulator && (valuesToSend = await submittedValuesManipulator(valuesToSend));

    if (!Object.keys(valuesToSend).length) {
      return;
    }

    try {
      const mutationResponse = await mutation.mutateAsync({
        id: record.id,
        values: valuesToSend as Partial<T>
      });

      setIsEditing(false);

      return mutationResponse;
    } catch (error) {
      console.error(error);
    }
  };

  const renderController = (recordField: Field<T>) => {
    if (recordField.control && !values[recordField.control]) {
      return null;
    }

    const foreignKeyFieldSuffix = '_id';
    const endsWith_id = recordField.key.endsWith(foreignKeyFieldSuffix);
    const shouldGetInitialValueForWriteOnlyField =
      endsWith_id && !Object.keys(dirtyFields).includes(recordField.key);

    /**
     * Get initial value for write-only foreign key id fields
     * Some foreign key field values are passed in as a mismatch to the value that comes serialized
     * in the person object. This is a bit of a hack, and is intended to only be temporary until we
     * implement an array of fields for person staff relationships.
     *
     * Ex. iop_counselor_id is passed to the editor but there is no value for it in the person object.
     * We get the value from iop_counselor instead.
     */
    const getInitialValueForWriteOnlyField = (value: string) => {
      interface FieldWithId {
        id: string;
      }
      interface RecordWithKeySignature extends Person {
        [key: string]: unknown;
      }
      const key = recordField.key.replace(foreignKeyFieldSuffix, '');

      return (
        ((record as unknown as RecordWithKeySignature)[key] as FieldWithId | null)?.id ?? value
      );
    };

    // Exempt the insurance_policy.plan_end_date field from being immutable
    const isMutableField =
      recordType === RecordType.InsurancePolicy &&
      INSURANCE_POLICY_NOT_MUTABLE_EXEMPT_FIELDS.includes(recordField.key)
        ? true
        : isMutable;

    return (
      <div key={recordField.key}>
        {get(errors, recordField.key)?.message && (
          <Alert level="error" message={String(get(errors, recordField.key)?.message)} />
        )}
        <div className={recordField.className || ''}>
          <Controller
            control={control}
            name={recordField.key}
            render={({ field }) => (
              <InputGroup>
                <FieldEditor
                  field={recordField}
                  isMutable={isMutableField}
                  onChange={field.onChange}
                  value={
                    shouldGetInitialValueForWriteOnlyField
                      ? (getInitialValueForWriteOnlyField(field.value) as FieldValue<T>)
                      : field.value
                  }
                />
                {recordField.additionalCaption && (
                  <p className="m-1 text-xs w-full">
                    {recordField.additionalCaption.label && (
                      <strong>{recordField.additionalCaption.label}: </strong>
                    )}
                    {recordField.additionalCaption.value}
                  </p>
                )}
              </InputGroup>
            )}
            rules={recordField.rules}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      {isEditing ? (
        <form onSubmit={handleSubmit(handleSave)}>
          <div className="flex justify-between mb-1">
            {!isMutable && <FontAwesomeIcon icon={faLock} className="ml-1 text-red-600" />}

            {!isMutable && isMutableOverrides && (
              <Button
                ariaLabel="Unlock record"
                color={Color.WHITE}
                onClick={() =>
                  confirm('Are you sure you want to unlock and make edits to this record?')
                    ? setIsMutable(true)
                    : null
                }
                size="xs"
                type="button">
                <FontAwesomeIcon icon={faUnlock} className="mr-1" />
                Unlock
              </Button>
            )}
          </div>

          {!isMutable && <Alert level="warn" message={cannotBeEditedMessage} />}
          {newRecord && <Heading value={3} text="New" />}

          {isFieldsArray ? (
            <>
              {visibleFields.map(renderController)}

              {hasHiddenFields && (
                <>
                  <div className="flex justify-center mt-3">
                    <Button
                      ariaLabel={`Show ${isExpanded ? 'less' : 'more'}`}
                      disabled={!isMutable}
                      size="xs"
                      onClick={() => setIsExpanded(!isExpanded)}>
                      Show {isExpanded ? 'less' : 'more'}
                      <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className="ml-2"
                      />
                    </Button>
                  </div>

                  {isExpanded && hiddenFields.map(renderController)}
                </>
              )}
            </>
          ) : (
            <>
              {get(errors, fields.key)?.message && (
                <Alert level="error" message={String(get(errors, fields.key)?.message)} />
              )}
              <ArrayFieldEditor
                control={control}
                fieldKey={fields.key}
                fields={fields.fields}
                newObject={fields.newObject}
                errors={errors}
                getFormValue={getValues}
                deleteConfirmMessage={fields.deleteConfirmMessage}
              />
            </>
          )}

          {/* Renders a custom message in case of validation errors */}
          {checkValidationMessage && checkValidationMessage(values)}

          {/* Render schema test errors */}
          {Object.values(errors).length > 0 &&
            Object.entries(errors).map(([key, error], index) => {
              if (error && 'root' in error && error.root?.message) {
                return <Alert level="error" message={String(error.root.message)} key={index} />;
                // If an error exists, but is not in the fields array, display it as a root message
              } else if (isFieldsArray && !fields.find(({ key: k }) => k === key)) {
                return error?.message ? (
                  <Alert level="error" message={String(error.message)} key={index} />
                ) : null;
              }
            })}

          <div className="flex justify-between items-center mt-3">
            <Button
              ariaLabel="Save"
              className="w-3/4 mr-3 flex items-center justify-center"
              color="white"
              loading={mutation.isPending}
              loadingWithSpinner={false}
              size="sm"
              type="submit">
              <FontAwesomeIcon icon={faSave} className="mr-2" /> Save
            </Button>

            <Button
              ariaLabel="Cancel"
              color="white"
              className="flex items-center"
              loading={mutation.isPending}
              loadingWithSpinner={false}
              size="sm"
              onClick={() => setIsEditing(false)}>
              <FontAwesomeIcon icon={faTimes} className="mr-2" /> Cancel
            </Button>
          </div>
        </form>
      ) : newRecord && !disableNew ? (
        <>
          <div className="flex items-center">
            <Button
              ariaLabel={newRecordButtonText}
              color="white"
              onClick={() => setIsEditing(true)}
              size="xs">
              <FontAwesomeIcon icon={faPlus} />
              {newRecordButtonText}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex items-center">
          <div
            className="grow max-w-full overflow-hidden"
            onDoubleClick={() => doubleClickToEdit && handleDoubleClick()}>
            {children}
          </div>

          <div
            className={`${
              subsectionEditor
                ? 'absolute top-0 right-0 group-hover/subsection:visible'
                : sectionEditor
                  ? 'absolute top-3 right-0 group-hover/section:visible'
                  : 'ml-3 group-hover/row:visible'
            } invisible space-x-1 ${editButtonClass}`}>
            <Button
              ariaLabel="Edit"
              color="white"
              disabled={disableEdit}
              onClick={() => setIsEditing(true)}
              size="xs">
              <FontAwesomeIcon icon={faPencil} />
            </Button>

            {deleteMutation && (
              <Button
                ariaLabel="Delete"
                disabled={disableEdit || !isMutable}
                color={Color.RED}
                onClick={() =>
                  confirm('Are you sure you want to delete this record?')
                    ? deleteMutation.mutateAsync(record.id)
                    : null
                }
                size="xs">
                <FontAwesomeIcon
                  className={deleteMutation.isPending ? 'animate-spin' : ''}
                  icon={deleteMutation.isPending ? faSpinner : faTrash}
                />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InPlaceEditor;
